<template>
  <v-container
    id="invoices"
    tag="section"
  >
    <base-material-card
      color="indigo"
      icon="mdi-file-table-box-multiple"
      inline
      class="px-5 py-3"
      top-actions
    >
      <template v-slot:after-heading>
        <div class="display-2 font-weight-light">
          {{ $t('Invoices') }}
        </div>
      </template>

      <template v-slot:top-actions>
        <debounce-v-btn
          class="px-2 ml-1 v-btn--tight"
          color="info"
          @click="loadInvoices()"
        >
          {{ $t('General.Buttons.Refresh') }}
        </debounce-v-btn>
         <debounce-v-btn
          class="px-2 ml-1 v-btn--tight"
          color="info"
          @click="ExportInvoices()"
        >
          {{ $t('General.Buttons.Export') }}
        </debounce-v-btn>
      </template>
      <v-row
        class="pt-4"
      >
        <v-col
          cols="12"
          sm="6"
        >
          <v-autocomplete
            v-model="searchValueResellerUid"
            color="light-blue lighten-1"
            item-color="light-blue lighten-1"
            :label="$t('Pages.Invoices.Form.Labels.ResellerFilter')"
            hide-details
            :items="resellers"
            prepend-icon="mdi-shopping"
            item-text="name"
            item-value="uid"
            clearable
            :disabled="loadingInvoices"
            @click:clear="searchValueResellerUid = null"
          />
        </v-col>
        <v-col
          cols="12"
          sm="6"
        >
          <v-menu
            ref="datemenu"
            v-model="datemenu"
            :close-on-content-click="false"
            :return-value.sync="searchDates"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="searchDatesText"
                :label="$t('Pages.Invoices.Form.Labels.DateFilter')"
                prepend-icon="mdi-calendar"
                :disabled="loadingInvoices"
                readonly
                clearable
                v-bind="attrs"
                v-on="on"
                @click:clear="clearDates"
              />
            </template>
            <v-date-picker
              v-model="searchDates"
              :max="currentDate"
              color="info"
              no-title
              scrollable
              range
            >
              <v-btn
                text
                @click="datemenu = false"
              >
                {{ $t('General.Buttons.Cancel') }}
              </v-btn>
              <v-btn
                text
                color="info"
                @click="setDatesFilter(searchDates)"
              >
                {{ $t('Pages.Invoices.Form.Buttons.PickDates') }}
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>
      </v-row>

      <v-row class="text-right">
        <v-spacer />
        <debounce-v-btn
          color="error"
          :disabled="!filtersSelected"
          @click="resetFilters"
        >
          {{ $t('General.Buttons.ClearFilters') }}
        </debounce-v-btn>
        <debounce-v-btn
          color="success"
          @click="loadFilterInvoices"
        >
          <v-icon left>
            mdi-filter
          </v-icon> {{ $t('General.Buttons.FilterEntity', ['invoices']) }}
        </debounce-v-btn>
      </v-row>

      <v-divider class="mt-3" />

      <v-data-table
        class="row-action"
        :headers="headers"
        :hide-default-header="true"
        :items="invoices"
        :sort-desc="[false, false]"
        :server-items-length="paging.totalResultCount"
        :page.sync="currentPage"
        :items-per-page.sync="itemsPerPage"
        :loading="loadingInvoices"
        :loading-text="$t('Pages.Invoices.Messages.OrdersLoadingMessage')"
        dense
        multi-sort
        @update:page="loadInvoices"
        @update:items-per-page="loadInvoices"
      >
        <template v-slot:header="{ props: { headers } }">
          <thead>
            <tr>
              <th
                v-for="(header, i) in headers"
                :key="`header-${i}`"
                :class="(i + 1 === headers.length ? 'text-right' : '')"
              >
                {{ $t(header.text) }}
              </th>
            </tr>
          </thead>
        </template>
        <template v-slot:item="{ item }">
          <tr @click="gotoDetails(item)">
            <td>
              <v-btn
                class="px-2 ml-1"
                color="info"
                rounded
                small
                @click="gotoDetails(item)"
              >
                {{ $t('General.Buttons.Details') }}
              </v-btn>
            </td>
            <td>{{ getResellerName(item.resellerId) }}</td>
            <td>{{ item.invoiceNumber }}</td>
            <td>{{ item.createDate | moment('DD/MM/YYYY') }}</td>
            <td>{{ item.issueDate | moment('DD/MM/YYYY') }}</td>
            <td class="text-right">
              <span class="mdi mdi-currency-eur" />{{ item.totalAmountInclVat.toFixed(2) }}
            </td>
          </tr>
        </template>
      </v-data-table>

      <template v-slot:actions>
        <v-row
          no-gutters
        >
          <debounce-v-btn
            class="px-2 ml-1"
            color="secondary"
            text
            @click="showCreateBillingPeriodsDialog()"
          >
            <v-icon left>
              mdi-account-cash
            </v-icon> {{ $t('Pages.Invoices.Buttons.CreateBillingPeriods') }}
          </debounce-v-btn>
          <debounce-v-btn
            class="px-2 ml-1"
            color="secondary"
            text
            @click="showProcessDraftInvoicesDialog()"
          >
            <v-icon left>
              mdi-ballot-recount
            </v-icon> {{ $t('Pages.Invoices.Buttons.ProcessDraftInvoices') }}
          </debounce-v-btn>
        </v-row>
      </template>
    </base-material-card>

    <v-dialog
      v-model="createBillingPeriodsDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title>
          {{ $t('Pages.Invoices.Dialogs.CreateBillingPeriods.Title') }}
          <v-spacer />
          <v-icon
            :aria-label="$t('General.Buttons.Close')"
            @click="cancelCreateBillingPeriods()"
          >
            mdi-close
          </v-icon>
        </v-card-title>

        <v-card-text
          class="text-center"
        >
          {{ $t('Pages.Invoices.Dialogs.CreateBillingPeriods.Message') }}
        </v-card-text>

        <v-card-text>
          <v-form ref="createBillingPeriodsForm">
            <v-container>
              <v-row>
                <v-col
                  cols="12"
                >
                  <v-autocomplete
                    v-model="createBillingPeriodsResellerUid"
                    color="light-blue lighten-1"
                    item-color="light-blue lighten-1"
                    :label="$t('Pages.Invoices.Dialogs.CreateBillingPeriods.Labels.ResellerOptional')"
                    hide-details
                    :items="resellers"
                    prepend-icon="mdi-shopping"
                    item-text="name"
                    item-value="uid"
                    clearable
                    :disabled="loadingInvoices"
                    @click:clear="createBillingPeriodsResellerUid = null"
                  />
                </v-col>
                <v-col
                  cols="12"
                >
                  <v-menu
                    ref="createBillingPeriodsDatemenu"
                    v-model="createBillingPeriodsDatemenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="createBillingPeriodsDatesText"
                        :label="$t('Pages.Invoices.Dialogs.CreateBillingPeriods.Labels.CutOffDateOptional')"
                        prepend-icon="mdi-calendar"
                        :disabled="loadingInvoices"
                        :hint="$t('Pages.Invoices.Dialogs.CreateBillingPeriods.Information.CutOffDateHint')"
                        :persistent-hint="true"
                        readonly
                        clearable
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="clearCreateBillingPeriodsDate"
                      />
                    </template>
                    <v-date-picker
                      v-model="createBillingPeriodsDate"
                      color="info"
                      no-title
                      scrollable
                    >
                      <v-btn
                        text
                        @click="createBillingPeriodsDatemenu = false"
                      >
                        {{ $t('General.Buttons.Cancel') }}
                      </v-btn>
                      <v-btn
                        text
                        color="info"
                        @click="setCreateBillingPeriodsDate(createBillingPeriodsDate)"
                      >
                        {{ $t('Pages.Invoices.Dialogs.CreateBillingPeriods.Buttons.PickDate') }}
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-card-text class="pb-6 pt-12 text-center">
          <v-btn
            class="mr-3"
            text
            @click="cancelCreateBillingPeriods()"
          >
            {{ $t('General.Buttons.Cancel') }}
          </v-btn>

          <debounce-v-btn
            color="success"
            text
            @click="createBillingPeriods()"
          >
            {{ $t('Pages.Invoices.Buttons.CreateBillingPeriods') }}
          </debounce-v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="processDraftInvoicesDialog"
      max-width="400"
    >
      <v-card>
        <v-card-title>
          {{ $t('Pages.Invoices.Dialogs.ProcessDraftInvoices.Title') }}
          <v-spacer />
          <v-icon
            :aria-label="$t('General.Buttons.Close')"
            @click="cancelProcessDraftInvoices()"
          >
            mdi-close
          </v-icon>
        </v-card-title>

        <v-card-text
          class="text-center"
        >
          {{ $t('Pages.Invoices.Dialogs.ProcessDraftInvoices.Message') }}
        </v-card-text>

        <v-card-text>
          <v-form ref="processDraftInvoicesForm">
            <v-container>
              <v-row>
                <v-col
                  cols="12"
                >
                  <v-autocomplete
                    v-model="processDraftInvoicesResellerUid"
                    color="light-blue lighten-1"
                    item-color="light-blue lighten-1"
                    :label="$t('Pages.Invoices.Dialogs.ProcessDraftInvoices.Labels.ResellerOptional')"
                    hide-details
                    :items="resellers"
                    prepend-icon="mdi-shopping"
                    item-text="name"
                    item-value="uid"
                    clearable
                    :disabled="loadingInvoices"
                    @click:clear="processDraftInvoicesResellerUid = null"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-card-text class="pb-6 pt-12 text-center">
          <v-btn
            class="mr-3"
            text
            @click="cancelProcessDraftInvoices()"
          >
            {{ $t('General.Buttons.Cancel') }}
          </v-btn>

          <debounce-v-btn
            color="success"
            text
            @click="processDraftInvoices()"
          >
            {{ $t('Pages.Invoices.Buttons.ProcessDraftInvoices') }}
          </debounce-v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import { mapGetters } from 'vuex'
  import adminInvoiceService from '@/services/Admin/invoiceService'
  import moment from 'moment'

  export default {
    data: () => ({
      headers: [
        {
          sortable: false,
          text: '',
          width: '1%',
        },
        {
          sortable: true,
          text: 'Pages.Invoices.TableHeaders.ResellerName',
        },
        {
          sortable: true,
          text: 'Pages.Invoices.TableHeaders.InvoiceNumber',
        },
        {
          sortable: true,
          text: 'Pages.Invoices.TableHeaders.CreateDate',
        },
        {
          sortable: true,
          text: 'Pages.Invoices.TableHeaders.IssueDate',
        },
        {
          sortable: true,
          text: 'Pages.Invoices.TableHeaders.TotalAmountInclVat',
        },
      ],
      invoices: [],
      paging: {},
      currentPage: 1,
      itemsPerPage: 10,
      loadingInvoices: false,
      searchValueResellerUid: null,
      datemenu: false,
      searchDates: [],
      createBillingPeriodsDialog: false,
      createBillingPeriodsResellerUid: null,
      createBillingPeriodsDatemenu: false,
      createBillingPeriodsDate: null,
      processDraftInvoicesDialog: false,
      processDraftInvoicesResellerUid: null,
    }),
    computed: {
      ...mapGetters(['getResellerName']),
      resellers () {
        return this.$store.state.resellers.list
      },
      searchDatesText () {
        return this.searchDates.map(d => moment(d).format('DD/MM/YYYY')).join(' - ')
      },
      createBillingPeriodsDatesText () {
        return this.createBillingPeriodsDate ? moment(this.createBillingPeriodsDate).format('DD/MM/YYYY') : ''
      },
      currentDate () {
        return moment().format('YYYY-MM-DD')
      },
      filtersSelected () {
        return (
          this.searchValueResellerUid != null ||
          this.searchDates.length > 0
        )
      },
    },
    created: async function () {
      if (this.$store.state.resellers.list.length === 0) {
        await this.$store.dispatch('RefreshResellers')
      }
      this.loadInvoices()
    },
    methods: {
      async loadInvoices () {
        this.loadingInvoices = true

        var dateTimeRange = this.parseDates()
        var pagedInvoicesApiResult = await adminInvoiceService.getInvoices(
          this.currentPage - 1,
          this.itemsPerPage,
          this.searchValueResellerUid,
          dateTimeRange
        )

        if (pagedInvoicesApiResult.success === false) {
          this.loadingInvoices = false
        }

        var pagedInvoices = pagedInvoicesApiResult.value
        this.invoices = pagedInvoices.data
        if (pagedInvoices.headers['x-pagination']) {
          this.paging = JSON.parse(pagedInvoices.headers['x-pagination'])
        }
        this.loadingInvoices = false
      },
      async ExportInvoices () {
        this.loadingInvoices = true

        var dateTimeRange = this.parseDates()
        var pagedInvoicesApiResult = await adminInvoiceService.getExportInvoices(
          this.searchValueResellerUid,
          dateTimeRange
        )

        if (pagedInvoicesApiResult.success === false) {
          this.loadingInvoices = false
        }
        this.loadingInvoices = false
      },
      async loadFilterInvoices () {
        this.currentPage = 1
        await this.loadInvoices()
      },
      gotoDetails (item) {
        this.$router.push({ name: 'Invoice', params: { invoiceUid: item.uid } }).catch(err => err)
      },
      clearDates () {
        this.searchDates = []
      },
      setDatesFilter (dates) {
        this.$refs.datemenu.save(dates)
      },
      resetFilters () {
        this.searchValueResellerUid = null
        this.clearDates()
        this.loadInvoices()
      },
      parseDates () {
        var dateTimeRange = []
        if (this.searchDates.length > 0) {
          dateTimeRange.push(moment.utc(this.searchDates[0]).toISOString())
          if (this.searchDates.length === 1) {
            dateTimeRange.push(moment.utc(this.searchDates[0]).endOf('day').toISOString())
          } else {
            dateTimeRange.push(moment.utc(this.searchDates[1]).endOf('day').toISOString())
          }
        }
        return dateTimeRange
      },
      showProcessDraftInvoicesDialog () {
        this.processDraftInvoicesDialog = true
      },
      async processDraftInvoices () {
        var apiResult = await adminInvoiceService.processDraftInvoices(this.processDraftInvoicesResellerUid)

        if (apiResult.success === false) {
          this.loadingInvoices = false
        }
        this.processDraftInvoicesDialog = false
      },
      cancelProcessDraftInvoices () {
        this.processDraftInvoicesDialog = false
        this.processDraftInvoicesResellerUid = null
      },
      showCreateBillingPeriodsDialog () {
        this.createBillingPeriodsDialog = true
      },
      async createBillingPeriods () {
        var cutOffDate = this.createBillingPeriodsDate ? moment.utc(this.createBillingPeriodsDate).toISOString() : null
        var apiResult = await adminInvoiceService.createBillingPeriods(this.createBillingPeriodsResellerUid, cutOffDate)

        if (apiResult.success === false) {
          this.loadingInvoices = false
        }

        this.createBillingPeriodsDialog = false
      },
      cancelCreateBillingPeriods () {
        this.createBillingPeriodsDialog = false
        this.createBillingPeriodsResellerUid = null
        this.createBillingPeriodsDatemenu = false
        this.createBillingPeriodsDate = null
      },
      clearCreateBillingPeriodsDate () {
        this.createBillingPeriodsDate = null
      },
      setCreateBillingPeriodsDate (dates) {
        this.$refs.createBillingPeriodsDatemenu.save(dates)
      },
    },
  }
</script>
